import { useMemo } from "react";
import { CopyIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AttributeKindEnum } from "@brewbase/db/types";
import type { BaseTemplateAttribute, Selection } from "./types";
import { useEditorConfig } from "./editor-config-context";
import { InspectorScalar } from "./inspector-scalar";
import { InspectorTabular } from "./inspector-tabular";
import { InspectorTabularColumn } from "./inspector-tabular-column";

interface InspectorPanelProps {
  selection: NonNullable<Selection>;
  attributes: BaseTemplateAttribute[];
  templateId: number;
  isLocked: boolean;
  onUpdateAttributeType: (id: number, updates: Record<string, unknown>) => void;
  onUpdateSettings: (id: number, settings: Record<string, unknown>) => void;
  onRemove: (id: number) => void;
  /** Omitted by domains without a duplicate action; hides the button. */
  onDuplicate?: (id: number) => void;
  onClose: () => void;
}

export function InspectorPanel({
  selection,
  attributes,
  templateId,
  isLocked: editorLocked,
  onUpdateAttributeType,
  onUpdateSettings,
  onRemove,
  onDuplicate,
  onClose,
}: InspectorPanelProps) {
  const { isGeneratedAttribute } = useEditorConfig();
  const attribute = useMemo(
    () => attributes.find((a) => a.id === selection.attributeId) ?? null,
    [attributes, selection.attributeId],
  );

  if (!attribute) return null;

  const isLocked = editorLocked || (isGeneratedAttribute?.(attribute) ?? false);

  const isTabularAttr =
    selection.type === "attribute" &&
    attribute.kind === AttributeKindEnum.TABULAR_DATA;

  const headerLabel =
    selection.type === "tabular-column"
      ? `${attribute.name || "Table"} > Column`
      : attribute.name || "Untitled";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border shrink-0">
        <span className="text-sm font-medium truncate">{headerLabel}</span>
        <div className="flex items-center gap-1 shrink-0">
          {onDuplicate && !isLocked && selection.type === "attribute" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-sm"
              onClick={() => onDuplicate(attribute.id)}
              title="Duplicate this attribute below itself"
            >
              <CopyIcon className="h-3.5 w-3.5" />
              Duplicate
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onClose}
          >
            <XIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        {selection.type === "tabular-column" ? (
          <InspectorTabularColumn
            templateAttributeId={selection.attributeId}
            columnSortOrder={selection.columnSortOrder}
            isLocked={isLocked}
            onClearSelection={onClose}
          />
        ) : isTabularAttr ? (
          <InspectorTabular
            attribute={attribute}
            isLocked={isLocked}
            onUpdateAttributeType={onUpdateAttributeType}
            onRemove={onRemove}
          />
        ) : (
          <InspectorScalar
            key={attribute.id}
            attribute={attribute}
            templateId={templateId}
            isLocked={editorLocked}
            onUpdateAttributeType={onUpdateAttributeType}
            onUpdateSettings={onUpdateSettings}
            onRemove={onRemove}
          />
        )}
      </ScrollArea>
    </div>
  );
}
